import { QueryClient } from '@tanstack/react-query';

import { ApiError, NetworkError } from './api';

// Defaults de TanStack Query para el QMS. networkMode 'always' (QA F1 #8): sin
// conexión, una consulta o un guardado NO se pausan esperando la red (eso dejaba la
// pantalla en skeleton infinito o el botón en "Guardando…" para siempre): fallan, la
// pantalla muestra su error con "Reintentar" y, al volver la conexión, las consultas
// se recargan solas (refetchOnReconnect). El aviso de conexión lo da OfflineBanner.

// Reintentos (manual UX §1.3, UX-ERR-01): una consulta se reintenta 2 veces con espera
// creciente solo si el servidor falló (5xx) o la red no respondió (NetworkError, ver api.ts).
// Un 4xx no se reintenta: repetirlo da el mismo resultado y solo demora el mensaje. Tampoco
// un error de contrato (Zod) o una cancelación. Los guardados nunca se reintentan solos.
const QUERY_RETRIES = 2;
const MAX_RETRY_DELAY_MS = 8_000;

export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (failureCount >= QUERY_RETRIES) {
    return false;
  }
  if (error instanceof ApiError) {
    return error.status >= 500;
  }
  return error instanceof NetworkError;
}

/** Espera antes del reintento N (desde 0): 1 s, 2 s, 4 s..., hasta 8 s. */
export function queryRetryDelay(attempt: number): number {
  return Math.min(1000 * 2 ** attempt, MAX_RETRY_DELAY_MS);
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000, // D4-006
        retry: shouldRetryQuery,
        retryDelay: queryRetryDelay,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        networkMode: 'always',
      },
      mutations: {
        retry: 0,
        networkMode: 'always',
      },
    },
  });
}
